import * as React from "react";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { FormattedRelativeTime } from "react-intl";
import { Duration } from "./duration";
import { NaiveDate } from "./date";

const padNum = (num: number, size: number): string => {
  return num.toString().padStart(size, "0");
};

export class Time {
  hours: number;
  minutes: number;
  seconds: number;
  milliseconds: number;
  nanoseconds: number;
  constructor(
    hours: number = 0,
    minutes: number = 0,
    seconds: number = 0,
    milliseconds: number = 0,
    nanoseconds: number = 0,
  ) {
    this.hours = hours;
    this.minutes = minutes;
    this.seconds = seconds;
    this.milliseconds = milliseconds;
    this.nanoseconds = nanoseconds;
  }
  static fromDuration(duration: Duration): Time {
    const { hours, minutes, seconds, milliseconds, nanoseconds } =
      duration.toFormatArgs();
    return new Time(hours, minutes, seconds, milliseconds, nanoseconds);
  }
  static fromDate(date: Date): Time {
    return new Time(
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds(),
    );
  }
  toString() {
    const out = `${padNum(this.hours, 2)}:${padNum(this.minutes, 2)}:${padNum(this.seconds, 2)}`;
    if (this.milliseconds === 0 && this.nanoseconds === 0) {
      return out;
    }
    const decimals =
      `${padNum(this.milliseconds, 3)}${padNum(this.nanoseconds, 3)}`.replace(
        /\.0+$/,
        "",
      );
    return `${out}.${decimals}`;
  }
  toDate() {
    return new Date(
      1970,
      0,
      1,
      this.hours,
      this.minutes,
      this.seconds,
      this.milliseconds,
    );
  }
}

export const useTimezones = (): string[] => {
  return React.useMemo(() => {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone");
    }
    return [];
  }, []);
};

export const useResolvedTimezone = (): string => {
  return React.useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    [],
  );
};

const DEFAULT_REFRESH_INTERVAL = 60;

type FormattedRelativeTimeProps = React.ComponentProps<
  typeof FormattedRelativeTime
>;

export const getTimeDifference = (
  date: Date,
  now: number = Date.now(),
): {
  value: number;
  unit: FormattedRelativeTimeProps["unit"];
  refreshInterval?: number;
} => {
  // Positive for past dates, negative for future ones.
  const timeDifferenceInSeconds = (now - date.getTime()) / 1000;
  const absTimeDifferenceInSeconds = Math.abs(timeDifferenceInSeconds);

  if (absTimeDifferenceInSeconds < 60) {
    // Less than 1 minute
    return {
      value: Math.round(timeDifferenceInSeconds),
      unit: "second",
      refreshInterval: DEFAULT_REFRESH_INTERVAL,
    };
  } else if (absTimeDifferenceInSeconds < 60 * 60) {
    // Less than 1 hour
    return {
      value: Math.round(timeDifferenceInSeconds / 60),
      unit: "minute",
      refreshInterval: DEFAULT_REFRESH_INTERVAL,
    };
  } else if (absTimeDifferenceInSeconds < 24 * 60 * 60) {
    // Less than 1 day
    return {
      value: Math.round(timeDifferenceInSeconds / (60 * 60)),
      unit: "hour",
      refreshInterval: DEFAULT_REFRESH_INTERVAL,
    };
  } else if (absTimeDifferenceInSeconds < 31 * 24 * 60 * 60) {
    // Less than 1 month
    return {
      value: Math.round(timeDifferenceInSeconds / (24 * 60 * 60)),
      unit: "day",
    };
  } else if (absTimeDifferenceInSeconds < 12 * 31 * 24 * 60 * 60) {
    // Less than 1 year
    return {
      value: Math.round(timeDifferenceInSeconds / (31 * 24 * 60 * 60)),
      unit: "month",
    };
  } else {
    // More than 1 year
    return {
      value: Math.round(timeDifferenceInSeconds / (12 * 31 * 24 * 60 * 60)),
      unit: "year",
    };
  }
};

/// The instant at `time` (HH:mm) on `date` in `timezone`, as an ISO string.
export const fromZonedDateTime = (
  date: NaiveDate,
  time: string,
  timezone: string,
): string => fromZonedTime(`${date.format()}T${time}`, timezone).toISOString();

/// The date and time (HH:mm) of the ISO string `value` in `timezone`.
export const toZonedDateTime = (
  value: string,
  timezone: string,
): { date: NaiveDate; time: string } => ({
  date: NaiveDate.parse(formatInTimeZone(value, timezone, "yyyy-MM-dd")),
  time: formatInTimeZone(value, timezone, "HH:mm"),
});
